import React, { useState, useEffect } from "react";
import "./LoginPage.css";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaCheckCircle,
  FaSpinner,
  FaHospitalAlt
} from "react-icons/fa";
import ENDPOINTS from "../Constants/ApiEndpoints";
import { useLocation, useNavigate } from "react-router-dom";

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [hideLogin, setHideLogin] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/outpatient/dashboard";

  const [error, setError] = useState(() => {
    if (location.state?.sessionExpired) {
      return "Your session has expired. Please sign in again to continue.";
    }
    return "";
  });

  useEffect(() => {
    if (location.state?.sessionExpired) {
      setError("Your session has expired. Please sign in again to continue.");
    }
  }, [location.state?.sessionExpired]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError("Please enter both username and password.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // Step 1: Call login API — sets the JWT as a secure HTTP-only cookie
      const loginRes = await fetch(`${ENDPOINTS.LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (!loginRes.ok) {
        throw new Error("Invalid username or password");
      }

      // Step 2: Call validate API — browser sends cookie automatically
      const validateRes = await fetch(`${ENDPOINTS.VALIDATE}`, {
        method: "POST",
        credentials: "include",
      });

      if (!validateRes.ok) {
        throw new Error("Token validation failed");
      }

      const userData = await validateRes.json();

      // Step 3: Update UI on success
      setLoggedIn(true);
      setTimeout(() => {
        setHideLogin(true);
        setTimeout(() => {
          onLogin(userData);
          navigate(from, { replace: true });
        }, 600);
      }, 1100);
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Invalid Credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`hospital-login-viewport ${hideLogin ? "login-viewport-hide" : ""}`}>
      {/* Dynamic Ambient Medical Glow Orbs */}
      <div className="login-ambient-orb orb-1" />
      <div className="login-ambient-orb orb-2" />
      <div className="login-ambient-orb orb-3" />

      {/* Main Elevated Split Card */}
      <div className={`hospital-login-card ${loggedIn ? "card-success" : ""}`}>
        {/* ── Left Panel: Brand & Doctor Showcase ── */}
        <div className="login-brand-panel">
          <div className="brand-panel-grid-pattern" />

          {/* Top Branding */}
          <div className="brand-header">
            <div className="brand-logo-container">
              <img
                src="/VThospitalsLogo.png"
                alt="VT Hospitals Logo"
                className="brand-hospital-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/VThospitalsIcon.png";
                }}
              />
            </div>
            <div className="system-status-pill">
              <span className="pulse-dot" />
              <span>SYSTEM ONLINE</span>
            </div>
          </div>

          {/* Precise Theory / Mission Statement */}
          <div className="brand-content-body">
            <h2 className="brand-title">
              Compassionate Care,
              <br />
              <span className="gradient-text">Engineered for Excellence.</span>
            </h2>

            <p className="brand-subtitle">
              Unified hospital management infrastructure powering admissions, real-time ward
              occupancy, and precision clinical care.
            </p>

            {/* Core Capability Badges */}
            <div className="brand-capabilities">
              <div className="capability-item">
                <span className="cap-icon">✓</span>
                <span className="cap-text">OPD & Inpatient Operations</span>
              </div>
              <div className="capability-item">
                <span className="cap-icon">✓</span>
                <span className="cap-text">Real-Time Ward & Bed Tracker</span>
              </div>
              <div className="capability-item">
                <span className="cap-icon">✓</span>
                <span className="cap-text">Integrated Pharmacy & Invoicing</span>
              </div>
            </div>
          </div>

          {/* Seamlessly Integrated Doctor Card & Photo */}
          <div className="brand-doctor-showcase">
            <div className="doctor-badge-card">
              <div className="doctor-info-text">
                <div className="doctor-status-row">
                  <span className="doctor-status-dot" />
                  <span className="doctor-status-label">On Duty · Consultation</span>
                </div>
                <h4 className="doctor-name">Dr. Viswanath</h4>
                <p className="doctor-role">Managing Director & Physician</p>
              </div>
            </div>

            {/* Photo anchored cleanly inside panel - ZERO OVERLAP with form */}
            <div className="doctor-photo-wrapper">
              <img
                src="/viswab.png"
                alt="Dr. Viswanath"
                className="doctor-photo-img"
              />
            </div>
          </div>
        </div>

        {/* ── Right Panel: Authentication Form ── */}
        <div className="login-form-panel">
          {!loggedIn ? (
            <div className="login-form-content">
              {/* Form Header */}
              <div className="form-header">
                <div className="form-access-badge">
                  <FaHospitalAlt className="badge-icon" />
                  <span>STAFF ACCESS PORTAL</span>
                </div>
                <h3 className="form-heading">Welcome Back</h3>
                <p className="form-subheading">
                  Enter your credentials to access your hospital account
                </p>
              </div>

              {/* Error Notification Banner */}
              {error && (
                <div className="login-error-banner" role="alert">
                  <span className="error-icon">!</span>
                  <span className="error-text">{error}</span>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleLogin} className="auth-form" noValidate>
                {/* Username Field */}
                <div className="input-field-group">
                  <label htmlFor="login-username" className="field-label">
                    Username / Staff ID
                  </label>
                  <div className="input-control-box">
                    <span className="field-icon">
                      <FaUser />
                    </span>
                    <input
                      id="login-username"
                      type="text"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="input-field-group">
                  <div className="field-label-row">
                    <label htmlFor="login-password" className="field-label">
                      Password
                    </label>
                  </div>
                  <div className="input-control-box">
                    <span className="field-icon">
                      <FaLock />
                    </span>
                    <input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Remember & Help */}
                <div className="form-utility-row">
                  <label className="remember-checkbox-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                    />
                    <span className="checkbox-custom" />
                    <span className="remember-text">Remember this session</span>
                  </label>
                  <button
                    type="button"
                    className="forgot-link-btn"
                    onClick={() =>
                      alert("Please contact hospital IT administration to reset your staff credentials.")
                    }
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`submit-login-btn ${loading ? "btn-loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="btn-loading-content">
                      <FaSpinner className="btn-spinner" />
                      <span>Authenticating...</span>
                    </span>
                  ) : (
                    <span>Sign In to Portal →</span>
                  )}
                </button>
              </form>

              {/* Security Trust Footer */}
              <div className="form-security-footer">
                <FaShieldAlt className="shield-icon" />
                <span>256-Bit SSL Encrypted · Certified Hospital Network</span>
              </div>
            </div>
          ) : (
            /* Success Transition State */
            <div className="login-success-state">
              <div className="success-icon-ring">
                <FaCheckCircle className="success-check-icon" />
              </div>
              <h3 className="success-title">Authentication Verified</h3>
              <p className="success-desc">
                Welcome, <strong>{username || "Staff Member"}</strong>. Preparing your clinical
                workspace...
              </p>
              <div className="success-loader-bar">
                <div className="success-loader-fill" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
